import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { BookOpen, DollarSign, Users, TrendingUp, Loader2, AlertCircle } from "lucide-react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";

const AdminBooksPage = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.email === "ai.manifest.official@gmail.com";
  const [books, setBooks] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/earn/admin/books`, { withCredentials: true, headers })
      .then(r => { setBooks(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (!isAdmin) {
    return (
      <DashboardLayout currentPage="admin-books">
        <div className="p-6 text-center"><p className="text-red-400">Access denied.</p></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="admin-books">
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white mb-2">Admin Books</h1>
          <p className="text-aura-text-secondary">Business overview and financial reporting.</p>
        </div>

        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-aura-emerald mx-auto" /></div>
        ) : books ? (
          <div className="space-y-6">
            {/* Subscriber Counters */}
            <div className="grid grid-cols-3 gap-4">
              <MetricCard icon={Users} label="Total Paid" value={books.subscribers.total_paid} accent="emerald" />
              <MetricCard icon={Users} label="Total Free" value={books.subscribers.total_free} accent="secondary" />
              <MetricCard icon={Users} label="Total Users" value={books.subscribers.total} accent="gold" />
            </div>

            {/* Revenue */}
            <div className="card-aura p-5">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-5 h-5 text-aura-emerald" />
                <h2 className="text-white font-semibold">Revenue</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatBox label="Weekly" value={`$${books.revenue.weekly.toFixed(2)}`} />
                <StatBox label="Monthly" value={`$${books.revenue.monthly.toFixed(2)}`} />
                <StatBox label="Yearly" value={`$${books.revenue.yearly.toFixed(2)}`} />
                <StatBox label="GST (Monthly)" value={`$${books.revenue.gst_estimate_monthly.toFixed(2)}`} />
                <StatBox label="Net After GST" value={`$${books.revenue.net_after_gst_monthly.toFixed(2)}`} />
                <StatBox label="Forecasted MRR" value={`$${books.revenue.forecasted_monthly_recurring.toFixed(2)}`} />
                <StatBox label="Forecasted ARR" value={`$${books.revenue.forecasted_yearly.toFixed(2)}`} />
                <StatBox label="Conversion Rate" value={`${books.conversion.signup_to_paid_rate}%`} />
              </div>
            </div>

            {/* Affiliate Finance */}
            {books.affiliate_liabilities && (
              <div className="card-aura p-5">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-aura-gold" />
                  <h2 className="text-white font-semibold">Affiliate Liabilities</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatBox label="Pending Payouts" value={`$${(books.affiliate_liabilities.pending_commissions || 0).toFixed(2)}`} />
                  <StatBox label="Outstanding Balance" value={`$${(books.affiliate_liabilities.eligible_payouts || 0).toFixed(2)}`} />
                  <StatBox label="Paid to Date" value={`$${(books.affiliate_liabilities.paid_payouts || 0).toFixed(2)}`} />
                  <StatBox label="Next Month Est." value={`$${(books.affiliate_liabilities.locked_commissions || 0).toFixed(2)}`} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="card-aura p-6 text-center"><p className="text-red-400">Failed to load data.</p></div>
        )}
      </div>
    </DashboardLayout>
  );
};

const MetricCard = ({ icon: Icon, label, value, accent }) => {
  const accents = { emerald: "text-aura-emerald bg-aura-emerald/10", gold: "text-aura-gold bg-aura-gold/10", secondary: "text-aura-text-secondary bg-white/5" };
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-aura p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accents[accent]}`}><Icon className="w-5 h-5" /></div>
      <div><p className="text-2xl font-bold text-white">{value}</p><p className="text-xs text-aura-text-muted">{label}</p></div>
    </motion.div>
  );
};

const StatBox = ({ label, value }) => (
  <div className="bg-white/5 rounded-lg p-3 text-center">
    <p className="text-lg font-semibold text-white">{value}</p>
    <p className="text-xs text-aura-text-muted">{label}</p>
  </div>
);

export default AdminBooksPage;
